/**
 * Holds the stateless authentication state of the current browser tab: the signed JWT issued by the backend login and the guid of the client the
 * user is currently working in. Both are sent on every authenticated backend request (see AuthInterceptor) as "Authorization: Bearer <jwt>" and
 * "X-Client-Guid: <guid>".
 *
 * The values live in the browser's own sessionStorage (the Web Storage API - not to be confused with the backend's former SessionStorage class,
 * which no longer exists): they are gone once the tab is closed, survive a reload within the same tab, and - unlike a cookie - are never replayed
 * cross-origin. The login app writes the very same keys before it redirects here, which is how the token is handed over between the two apps.
 *
 * @namespace vamos.cockpit.base.services.type
 */
export default class AuthService {

    private static readonly TOKEN_KEY = "vamos.auth.token";

    private static readonly CLIENT_GUID_KEY = "vamos.auth.clientGuid";

    public static getToken(): string | null {
        return window.sessionStorage.getItem(AuthService.TOKEN_KEY);
    }

    public static setToken(token: string): void {
        window.sessionStorage.setItem(AuthService.TOKEN_KEY, token);
    }

    public static getClientGuid(): string | null {
        return window.sessionStorage.getItem(AuthService.CLIENT_GUID_KEY);
    }

    public static setClientGuid(clientGuid: string): void {
        window.sessionStorage.setItem(AuthService.CLIENT_GUID_KEY, clientGuid);
    }

    /**
     * Logging out is a purely client-side operation - the backend keeps no session state that could be invalidated, the token simply stops being
     * sent (and expires on its own).
     */
    public static clear(): void {
        window.sessionStorage.removeItem(AuthService.TOKEN_KEY);
        window.sessionStorage.removeItem(AuthService.CLIENT_GUID_KEY);
    }
}
